import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "@/App.css";
import { Toaster } from "./components/ui/sonner";

// Legal & Utility Components
import { PrivacyPolicy, TermsOfUse, CookiePolicy, AccessibilityStatement } from "./components/LegalPages";
import CookieBanner from "./components/CookieBanner";
import AccessibilityWidget from "./components/AccessibilityWidget";
import AIChatWidget from "./components/AIChatWidget";
import FeedbackWidget from "./components/FeedbackWidget";
import { MaintenancePage } from "./components/MaintenancePage";
import { useLanguage } from "./contexts/LanguageContext";

// Layout
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";

// Sections
import { HeroSection } from "./components/sections/HeroSection";
import { PainSection } from "./components/sections/PainSection";
import { QuoteSection } from "./components/sections/QuoteSection";
import { AboutSection } from "./components/sections/AboutSection";
import { VisionSection } from "./components/sections/VisionSection";
import { TreatmentsSection } from "./components/sections/TreatmentsSection";
import { SloganSliderSection } from "./components/sections/SloganSliderSection";
import { BeforeAfterSection } from "./components/sections/BeforeAfterSection";
import { ProcessSection } from "./components/sections/ProcessSection";
import { StatsSection } from "./components/sections/StatsSection";
import { TestimonialsSection } from "./components/sections/TestimonialsSection";
import { FAQSection } from "./components/sections/FAQSection";
import { ContactSection } from "./components/sections/ContactSection";

// Shared
import { ErrorBoundary } from "./components/shared/ErrorBoundary";
import { ExitPopup } from "./components/shared/ExitPopup";
import { FloatingWhatsApp } from "./components/shared/FloatingWhatsApp";
import { MobileStickyBar } from "./components/shared/MobileStickyBar";

// Pages - Code Split (lazy loaded for better initial page performance)
const LaserPage = lazy(() => import("./pages/LaserPage"));
const ScarCheckerPage = lazy(() => import("./pages/ScarCheckerPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const BlogIndex = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.BlogIndex })));
const ArticlePage = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.ArticlePage })));

// Lightweight fallback shown during lazy route loads
const RouteFallback = () => (
  <div
    role="status"
    aria-live="polite"
    aria-busy="true"
    style={{ minHeight: '100vh', background: '#FAF7F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <div style={{
      width: 48, height: 48, border: '3px solid #E5E0DA',
      borderTopColor: '#C4956A', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
      טוען...
    </span>
  </div>
);

// Main App
function App() {
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(null);
  const [currentPage, setCurrentPage] = useState('main');
  const exitIntentShown = useRef(false);
  const { direction } = useLanguage();
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetch(`${API_URL}/api/maintenance-status`)
      .then(res => res.json())
      .then(data => setMaintenanceMode(data))
      .catch(() => setMaintenanceMode({ enabled: false }));
  }, [API_URL]);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage('main');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitIntentShown.current && window.innerWidth > 768) {
        if (!sessionStorage.getItem('exitPopupDismissed')) {
          exitIntentShown.current = true;
          setShowExitPopup(true);
        }
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  const handleCloseExitPopup = () => {
    setShowExitPopup(false);
    sessionStorage.setItem('exitPopupDismissed', 'true');
  };

  const handleOpenLegalPage = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleCloseLegalPage = (navigateTo) => {
    if (navigateTo && typeof navigateTo === 'string') {
      setCurrentPage(navigateTo);
    } else {
      setCurrentPage('main');
    }
    window.scrollTo(0, 0);
  };

  // Wait for maintenance status before rendering
  if (maintenanceMode === null) {
    return <div style={{ minHeight: '100vh', background: '#1a1a1a' }} />;
  }

  // Maintenance mode - block the site
  if (maintenanceMode?.enabled) return <MaintenancePage message={maintenanceMode.message} />;

  // Legal pages
  if (currentPage === 'privacy') return <PrivacyPolicy onClose={handleCloseLegalPage} />;
  if (currentPage === 'terms') return <TermsOfUse onClose={handleCloseLegalPage} />;
  if (currentPage === 'cookies') return <CookiePolicy onClose={handleCloseLegalPage} />;
  if (currentPage === 'accessibility') return <AccessibilityStatement onClose={handleCloseLegalPage} />;

  return (
    <div className="App pb-20 md:pb-0 overflow-x-hidden" dir={direction}>
      <Helmet>
        <html lang="he" dir="rtl" />
        <meta name="description" content="מזל גניאל - קוסמטיקאית פרא-רפואית מומחית בירושלים, פסגת זאב. 40 שנות ניסיון בטיפול צלקות מורכבות, צלקות ניתוח, כוויות ואקנה. הסרת שומות, לייזר IPL, איפור קבוע. ייעוץ ראשון חינם! 052-3824500" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://mazalganeel.co.il/" />
      </Helmet>
      <a href="#main-content" className="skip-link">דלג לתוכן הראשי</a>

      <Header />
      
      <main id="main-content" role="main">
        <HeroSection />
        <AboutSection />
        <VisionSection />
        <TreatmentsSection />
        <SloganSliderSection />
        <PainSection />
        <QuoteSection />
        <BeforeAfterSection />
        <ProcessSection />
        <StatsSection />
        <TestimonialsSection />
        <FAQSection />
        <ContactSection />
      </main>
      
      <Footer onOpenLegalPage={handleOpenLegalPage} />
      
      <FloatingWhatsApp />
      <MobileStickyBar />
      <AIChatWidget />
      <FeedbackWidget />
      <AccessibilityWidget onOpenAccessibilityPage={() => handleOpenLegalPage('accessibility')} />
      
      <CookieBanner onOpenCookiePolicy={() => handleOpenLegalPage('cookies')} />
      <ExitPopup isOpen={showExitPopup} onClose={handleCloseExitPopup} />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<App />} />
          <Route path="/laser" element={<LaserPage />} />
          <Route path="/scar-checker" element={<ScarCheckerPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<ArticlePage />} />
          <Route path="/laser.html" element={<LaserPage />} />
          <Route path="/scar-checker.html" element={<ScarCheckerPage />} />
          <Route path="/admin.html" element={<AdminPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
